import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Fixed } from 'layouts';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, Refresh, Search } from '@mui/icons-material';
import moment from 'moment';
import getBouncedEmails from 'redux/actions/Common/getBouncedEmails';
import updateBouncedEmailSuppression from 'redux/actions/Common/updateBouncedEmailSuppression';

const PAGE_SIZE = 50;

const BouncedEmails = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const [bouncedEmails, setBouncedEmails] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [fetchingPagination, setFetchingPagination] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Emails whose suppression toggle is currently in-flight.
  const [updating, setUpdating] = useState([]);
  const [alert, setAlert] = useState(null);

  const fetchFirstPage = () => {
    setFetching(true);
    dispatch(
      getBouncedEmails(
        PAGE_SIZE,
        null,
        (data) => {
          setBouncedEmails(data || []);
          setHasMore((data || []).length === PAGE_SIZE);
          setFetching(false);
        },
        (message) => {
          setFetching(false);
          setAlert({ severity: 'error', message });
        },
      ),
    );
  };

  const fetchNextPage = () => {
    const lastSeen = bouncedEmails?.[bouncedEmails.length - 1]?.email;
    if (!lastSeen) return;
    setFetchingPagination(true);
    dispatch(
      getBouncedEmails(
        PAGE_SIZE,
        lastSeen,
        (data) => {
          const page = data || [];
          // A stale/unknown lastSeen makes the backend silently restart from
          // page 1, so drop anything already on screen instead of duplicating.
          setBouncedEmails((existing) => {
            const seen = new Set(existing.map((item) => item?.email));
            return [...existing, ...page.filter((it) => !seen.has(it?.email))];
          });
          setHasMore(page.length === PAGE_SIZE);
          setFetchingPagination(false);
        },
        (message) => {
          setFetchingPagination(false);
          setAlert({ severity: 'error', message });
        },
      ),
    );
  };

  const handleToggleSuppressed = (email, suppressed) => {
    if (!email || updating.includes(email)) return;
    setUpdating((existing) => [...existing, email]);
    // Optimistic — reverted in the error callback below.
    setBouncedEmails((existing) =>
      existing.map((item) =>
        item?.email === email ? { ...item, suppressed } : item,
      ),
    );
    dispatch(
      updateBouncedEmailSuppression(
        email,
        suppressed,
        () => {
          setUpdating((existing) => existing.filter((it) => it !== email));
          setAlert({
            severity: 'success',
            message: suppressed
              ? `Sending to ${email} is now suppressed`
              : `Sending to ${email} has been re-enabled`,
          });
        },
        (message) => {
          setUpdating((existing) => existing.filter((it) => it !== email));
          setBouncedEmails((existing) =>
            existing.map((item) =>
              item?.email === email
                ? { ...item, suppressed: !suppressed }
                : item,
            ),
          );
          setAlert({ severity: 'error', message });
        },
      ),
    );
  };

  useEffect(() => {
    fetchFirstPage();
  }, []);

  const trimmedKeyword = keyword.trim().toLowerCase();
  const rows = bouncedEmails.filter((item) => {
    if (statusFilter === 'suppressed' && !item?.suppressed) return false;
    if (statusFilter === 'active' && item?.suppressed) return false;
    if (!trimmedKeyword) return true;
    return (
      item?.email?.toLowerCase()?.includes(trimmedKeyword) ||
      item?.reason?.toLowerCase()?.includes(trimmedKeyword)
    );
  });

  return (
    <Fixed>
      <Box
        width={{ xs: '100vw', sm: '100vw', md: 'calc(100vw - 266px)' }}
        height={{ xs: '100vh', sm: '100vh', md: 'calc(100vh - 80px)' }}
        sx={{
          overflow: 'scroll',
          padding: '20px',
        }}
        display={'flex'}
        flexDirection={'column'}
        position={'relative'}
      >
        <Box
          display={'flex'}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={'space-between'}
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
        >
          <Box>
            <Typography variant={'h6'} fontWeight={'bold'}>
              Bounced Emails
            </Typography>
            <Typography variant={'body2'} color={'text.secondary'}>
              Addresses that rejected our outbound mail. Suppressing one stops
              all future sends to it.
            </Typography>
          </Box>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <TextField
              size="small"
              placeholder="Filter loaded rows..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: isMd ? 260 : 'auto' }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={statusFilter}
              onChange={(e, value) => {
                if (value) setStatusFilter(value);
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="suppressed">Suppressed</ToggleButton>
              <ToggleButton value="active">Not suppressed</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Reload">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={fetchFirstPage}
                  disabled={fetching}
                  startIcon={<Refresh fontSize="small" />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {fetching ? (
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={1}
            py={6}
          >
            <CircularProgress size={20} />
            <Typography variant={'body2'} color={'text.secondary'}>
              Loading bounced emails...
            </Typography>
          </Box>
        ) : bouncedEmails?.length > 0 ? (
          <>
            <TableContainer component={Paper} sx={{ marginTop: 2 }}>
              <Table sx={{ minWidth: 650 }} aria-label="bounced emails table">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell align="center">Bounced On</TableCell>
                    <TableCell align="center">Suppressed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length > 0 ? (
                    rows.map((item) => (
                      <TableRow
                        key={item?.email}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          <Box
                            display={'flex'}
                            alignItems={'center'}
                            gap={1}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            <Typography variant={'body2'} fontWeight={'bold'}>
                              {item?.email || '--'}
                            </Typography>
                            <Tooltip title="Copy email">
                              <ContentCopy
                                fontSize="small"
                                sx={{
                                  cursor: 'pointer',
                                  color: 'text.secondary',
                                  width: 16,
                                }}
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    item?.email || '',
                                  );
                                  setAlert({
                                    severity: 'success',
                                    message: 'Email copied to clipboard',
                                  });
                                }}
                              />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          <Typography
                            variant={'body2'}
                            color={'text.secondary'}
                          >
                            {item?.reason || '--'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip
                            title={
                              item?.bouncedAt
                                ? moment(item.bouncedAt).format(
                                    'DD MMMM YYYY, hh:mm A',
                                  )
                                : ''
                            }
                          >
                            <Typography variant={'body2'}>
                              {item?.bouncedAt
                                ? moment(item.bouncedAt).format('DD MMM YYYY')
                                : '--'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            gap={1}
                          >
                            <Switch
                              size="small"
                              checked={Boolean(item?.suppressed)}
                              disabled={updating.includes(item?.email)}
                              onChange={(e) =>
                                handleToggleSuppressed(
                                  item?.email,
                                  e.target.checked,
                                )
                              }
                            />
                            {updating.includes(item?.email) ? (
                              <CircularProgress size={14} />
                            ) : (
                              <Chip
                                size="small"
                                label={item?.suppressed ? 'Blocked' : 'Sending'}
                                color={item?.suppressed ? 'error' : 'success'}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography
                          variant={'body2'}
                          color={'text.secondary'}
                          py={2}
                        >
                          No loaded rows match this filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {fetchingPagination ? (
              <Box
                display={'flex'}
                justifyContent={'center'}
                width={'100%'}
                p={'10px'}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled
                >
                  Loading more bounced emails ...
                  <CircularProgress size={20} sx={{ marginLeft: 1 }} />
                </Button>
              </Box>
            ) : hasMore ? (
              <Box
                display={'flex'}
                justifyContent={'center'}
                width={'100%'}
                p={'10px'}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={fetchNextPage}
                  sx={{ whiteSpace: 'nowrap', minWidth: 100 }}
                >
                  Load More
                </Button>
              </Box>
            ) : null}
          </>
        ) : (
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={1}
            py={6}
          >
            <Typography variant={'subtitle1'} fontWeight={'bold'}>
              No bounced emails
            </Typography>
            <Typography
              variant={'body2'}
              color={'text.secondary'}
              textAlign={'center'}
            >
              {/* The list endpoint returns 200 with [] on a downstream failure
                  too, so an empty list is genuinely ambiguous here. */}
              Nothing has bounced yet — or the notification service could not be
              reached. Try refreshing to rule the second one out.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={fetchFirstPage}
              startIcon={<Refresh fontSize="small" />}
            >
              Refresh
            </Button>
          </Box>
        )}

        <Snackbar
          open={Boolean(alert)}
          autoHideDuration={4000}
          onClose={() => setAlert(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setAlert(null)}
            severity={alert?.severity || 'info'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {alert?.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fixed>
  );
};

export default BouncedEmails;
